import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, PlusCircle, ExternalLink, FileText, Eye } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { format } from "date-fns";

const destLabel = (d) => (d === "success-stories" ? "Success Stories" : "Insights");
const destBadge = (d) =>
  d === "success-stories"
    ? "bg-royal/10 text-royal"
    : "bg-slate-100 text-slate-700";

export default function AdminDashboard() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blogs");
      setBlogs(data);
    } catch { toast.error("Failed to load blogs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/blogs/${id}`); toast.success("Article deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const published = blogs.filter((b) => b.status === "published").length;
  const drafts = blogs.filter((b) => b.status === "draft").length;
  const filtered = tab === "all" ? blogs : blogs.filter((b) => (b.destination || "insights") === tab);
  const tabs = [
    { key: "all", label: "All" },
    { key: "insights", label: "Insights" },
    { key: "success-stories", label: "Success Stories" },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Executive Insights CMS</p>
          <h1 className="mt-2 font-heading text-3xl font-light tracking-tight md:text-4xl">Articles.</h1>
          <p className="mt-2 font-body text-slate-500">{blogs.length} total, {published} published, {drafts} draft.</p>
        </div>
        <Link to="/adminia/blogs/new" data-testid="admin-new-blog-cta" className="group inline-flex items-center gap-2 rounded-full bg-royal px-6 py-3 font-body text-sm font-semibold text-white transition-colors hover:bg-navy">
          <PlusCircle className="h-4 w-4" /> New Article
        </Link>
      </div>

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`rounded-full px-4 py-2 font-body text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${
              tab === t.key ? "bg-royal text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white">
        {loading ? (
          <div className="p-10 text-center font-body text-sm text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="mx-auto h-10 w-10 text-slate-300" />
            <h3 className="mt-4 font-heading text-xl font-bold">No articles here yet</h3>
            <p className="mt-2 font-body text-sm text-slate-500">Create your first article for {tab === "all" ? "the studio" : destLabel(tab)}.</p>
            <Link to="/adminia/blogs/new" className="mt-6 inline-flex items-center gap-2 rounded-full bg-royal px-5 py-2.5 font-body text-sm font-semibold text-white hover:bg-navy">
              <PlusCircle className="h-4 w-4" /> Create article
            </Link>
          </div>
        ) : (
          <table className="w-full text-left">
            <thead className="border-b border-slate-200 bg-slate-50 font-body text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
              <tr>
                <th className="px-6 py-3">Title</th>
                <th className="px-6 py-3 hidden md:table-cell">Destination</th>
                <th className="px-6 py-3 hidden lg:table-cell">Updated</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                const dest = b.destination || "insights";
                const publicPath = dest === "success-stories" ? `/success-stories/${b.slug}` : dest === "insights" ? `/insights/${b.slug}` : null;
                return (
                <tr key={b.id} data-testid={`admin-blog-row-${b.slug}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="font-heading font-semibold text-navy">{b.title}</div>
                    <div className="mt-0.5 font-body text-xs text-slate-500">{publicPath || "private"}</div>
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell"><span className={`rounded-full px-2 py-1 font-body text-xs font-semibold ${destBadge(dest)}`}>{destLabel(dest)}</span></td>
                  <td className="px-6 py-4 hidden lg:table-cell font-body text-sm text-slate-500">{b.updated_at ? format(new Date(b.updated_at), "d MMM yyyy") : "—"}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center rounded-full px-3 py-1 font-body text-xs font-semibold ${b.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{b.status}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      {b.status === "published" && publicPath && (
                        <a href={publicPath} target="_blank" rel="noreferrer" title="View" className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-navy"><Eye className="h-4 w-4" /></a>
                      )}
                      <Link to={`/adminia/blogs/${b.id}/edit`} data-testid={`admin-edit-${b.slug}`} title="Edit" className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-navy"><Pencil className="h-4 w-4" /></Link>
                      <button onClick={() => remove(b.id, b.title)} data-testid={`admin-delete-${b.slug}`} title="Delete" className="rounded-full p-2 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
